// Financial Reporting suite — raw HTTP transport for the series catalog, the
// loan-tab / portfolio reporting overviews, and the PreparerReviewer instance
// ladder (create → submit → certify → file). Previews reuse
// loanReportsApi.previewUrl (shared engine endpoint).
//
// The catalog is not a fixed size (13 FR rows + DW-M1 + VG-M1 today, and
// retirements move rows between states) — derive any count shown to a user
// from the response, never from a literal.

import { api } from "../client";
import type { LoanReportDto } from "./loanReportsApi";

export type ReportSeriesActivationState = "Active" | "TriggerArmed" | "Dormant";
export type ReportSeriesCadence = "Monthly" | "Quarterly" | "Annual";
export type ReportSeriesLevel = "Loan" | "Portfolio";

export interface ReportSeriesDto {
  id: string;
  code: string;
  title: string;
  cadence: ReportSeriesCadence;
  deadlineRule: string;
  deadlineOffsetDays: number;
  deadlineOffsetIsBusinessDays: boolean;
  audience: string;
  level: ReportSeriesLevel;
  signerFlow: "PreparerReviewer" | "AuthorizedOfficer";
  governingSource: string;
  activationState: ReportSeriesActivationState;
  hasTemplate: boolean;
}

export type ReportingDueStatus = "upcoming" | "due" | "overdue";

export interface ReportingSeriesCardDto {
  code: string;
  title: string;
  cadence: ReportSeriesCadence;
  deadlineRule: string;
  audience: string;
  hasTemplate: boolean;
  nextPeriodStart: string;
  nextPeriodEnd: string;
  nextDueDate: string;
  dueStatus: ReportingDueStatus;
}

export interface ReportingStatsDto {
  dueThisMonth: number;
  overdue: number;
  completedYtd: number;
  onTimeRatePct: number | null;
}

export interface ReportingDueItemDto {
  seriesCode: string;
  seriesTitle: string;
  periodStart: string;
  periodEnd: string;
  dueDate: string;
  status: ReportingDueStatus;
}

export interface LoanReportingOverviewDto {
  stats: ReportingStatsDto;
  dueItems: ReportingDueItemDto[];
  series: ReportingSeriesCardDto[];
  instances: LoanReportDto[];
}

export type PortfolioReportingOverviewDto = LoanReportingOverviewDto;

export interface CreateReportInstanceRequest {
  seriesCode: string;
  periodStart: string;
  periodEnd: string;
  narrative?: string | null;
}

export interface UpdateSeriesActivationRequest {
  activationState: ReportSeriesActivationState;
  reason?: string | null;
}

/** The Defaults & Workout monthly status series (loan-level, Active). */
export const DW_SERIES_CODE = "DW-M1";

/** The Vendor Program monthly goal-progress series (loan-level, Active). */
export const VG_SERIES_CODE = "VG-M1";

/** DW-scoped create — no seriesCode: the endpoint pins DW-M1 so the
 *  Defaults.Manage grant (B4's preparer path) can only mint this series. */
export interface CreateDwReportInstanceRequest {
  periodStart: string;
  periodEnd: string;
  narrative?: string | null;
}

/** VG-scoped create — same shape, same series-pinning precedent: the
 *  endpoint pins VG-M1 so the Vendors.Manage grant (E1-VM's preparer path,
 *  no Loans.Manage) can only mint this series. */
export type CreateVgReportInstanceRequest = CreateDwReportInstanceRequest;

export const financialReportingApi = {
  /** Active + TriggerArmed series — dormant rows are absent by design. */
  series: () =>
    api.get<ReportSeriesDto[]>("/portfolio-admin/reporting/series"),
  /** Full catalog incl. Dormant (admin catalog view). */
  catalog: () =>
    api.get<ReportSeriesDto[]>("/portfolio-admin/reporting/series/catalog"),
  updateActivation: (code: string, body: UpdateSeriesActivationRequest) =>
    api.patch<ReportSeriesDto>(
      `/portfolio-admin/reporting/series/${encodeURIComponent(code)}/activation`,
      body,
    ),
  loanOverview: (loanId: string) =>
    api.get<LoanReportingOverviewDto>(
      `/portfolio-admin/loans/${loanId}/reporting/overview`,
    ),
  portfolioOverview: () =>
    api.get<PortfolioReportingOverviewDto>(
      "/portfolio-admin/reporting/portfolio/overview",
    ),
  createLoanInstance: (loanId: string, body: CreateReportInstanceRequest) =>
    api.post<LoanReportDto>(
      `/portfolio-admin/loans/${loanId}/reporting/instances`,
      body,
    ),
  createPortfolioInstance: (body: CreateReportInstanceRequest) =>
    api.post<LoanReportDto>("/portfolio-admin/reporting/portfolio/instances", body),
  submit: (reportId: string) =>
    api.post<LoanReportDto>(`/portfolio-admin/reporting/instances/${reportId}/submit`),
  // DW-scoped preparer ladder (Defaults.Manage — B4 holds no Loans.Manage).
  createDwInstance: (loanId: string, body: CreateDwReportInstanceRequest) =>
    api.post<LoanReportDto>(`/portfolio-admin/loans/${loanId}/dw-reports`, body),
  submitDwInstance: (reportId: string) =>
    api.post<LoanReportDto>(`/portfolio-admin/dw-reports/${reportId}/submit`),
  // VG-scoped preparer ladder (Vendors.Manage — E1-VM holds no Loans.Manage).
  createVgInstance: (loanId: string, body: CreateVgReportInstanceRequest) =>
    api.post<LoanReportDto>(`/portfolio-admin/loans/${loanId}/vg-reports`, body),
  submitVgInstance: (reportId: string) =>
    api.post<LoanReportDto>(`/portfolio-admin/vg-reports/${reportId}/submit`),
  certify: (reportId: string) =>
    api.post<LoanReportDto>(`/portfolio-admin/reporting/instances/${reportId}/certify`),
  file: (reportId: string) =>
    api.post<LoanReportDto>(`/portfolio-admin/reporting/instances/${reportId}/file`),
};
